// =====================================
// Frontend Notification Service Integration
// =====================================

import { io, Socket } from 'socket.io-client';

// Socket server URL. Was hardcoded to :3001, which is neither the frontend
// (:3000) nor the backend (:5001) — this service never connected to anything.
const WS_URL = import.meta.env.VITE_SOCKET_URL || 'http://localhost:5001';

// Notification types matching backend
export type NotificationType = 
  | 'order_created'
  | 'order_updated'
  | 'order_ready'
  | 'order_completed'
  | 'payment_received'
  | 'table_status_changed'
  | 'staff_alert'
  | 'system_alert'
  | 'kitchen_update'
  | 'customer_feedback';

export interface NotificationMessage {
  id: string;
  type: NotificationType;
  title: string;
  message: string;
  senderId: string;
  senderName: string;
  senderRole: string;
  restaurantId: string;
  timestamp: Date;
  priority: 'low' | 'normal' | 'high' | 'urgent' | 'critical';
  data?: any;
}

export interface NotificationServiceCallbacks {
  onConnect?: () => void;
  onDisconnect?: () => void;
  onAuthSuccess?: (data: any) => void;
  onAuthError?: (error: string) => void;
  onNotification?: (notification: NotificationMessage) => void;
  onOrderCreated?: (data: any) => void;
  onOrderUpdated?: (data: any) => void;
  onOrderReady?: (data: any) => void;
  onOrderCompleted?: (data: any) => void;
  onPaymentReceived?: (data: any) => void;
  onTableStatusChanged?: (data: any) => void;
  onKitchenUpdate?: (data: any) => void;
  onStaffAlert?: (data: any) => void;
  onSystemAlert?: (data: any) => void;
  onError?: (error: string) => void;
}

/**
 * Frontend Notification Service
 * Handles WebSocket communication with nova-cafe-backend notification system
 */
export class FrontendNotificationService {
  private static instance: FrontendNotificationService;
  private socket: Socket | null = null;
  private callbacks: NotificationServiceCallbacks = {};
  private isConnected: boolean = false;
  private authToken: string | null = null;
  private userInfo: {
    userId: string;
    name: string;
    role: string;
    restaurantId: string;
  } | null = null;

  private constructor() {}

  public static getInstance(): FrontendNotificationService {
    if (!FrontendNotificationService.instance) {
      FrontendNotificationService.instance = new FrontendNotificationService();
    }
    return FrontendNotificationService.instance;
  }

  /**
   * Initialize and connect to WebSocket server
   */
  public connect(
    authToken: string,
    userInfo: {
      userId: string;
      name: string;
      role: string;
      restaurantId: string;
    },
    callbacks: NotificationServiceCallbacks = {}
  ): void {
    this.authToken = authToken;
    this.userInfo = userInfo;
    this.callbacks = callbacks;

    // Disconnect existing connection
    if (this.socket) {
      this.disconnect();
    }

    this.socket = io(WS_URL, {
      transports: ['websocket', 'polling'],
      auth: {
        token: authToken,
      },
      timeout: 10000,
    });

    this.setupEventListeners();
  }

  /**
   * Disconnect from WebSocket server
   */
  public disconnect(): void {
    if (this.socket) {
      this.socket.disconnect();
      this.socket = null;
    }
    this.isConnected = false;
  }

  /**
   * Check if connected to WebSocket server
   */
  public isSocketConnected(): boolean {
    return this.isConnected && this.socket !== null;
  }

  /**
   * Send authentication data
   */
  public authenticate(): void {
    if (this.socket && this.userInfo) {
      this.socket.emit('authenticate', {
        userId: this.userInfo.userId,
        name: this.userInfo.name,
        role: this.userInfo.role,
        restaurantId: this.userInfo.restaurantId,
      });
    }
  }

  /**
   * Send order created notification
   */
  public notifyOrderCreated(orderData: {
    orderId: string;
    orderNumber: string;
    tableNumber: string;
    waiterId?: string;
    items: any[];
    total: number;
  }): void {
    if (this.socket) {
      this.socket.emit('order_created', orderData);
    }
  }

  /**
   * Send order status update notification
   */
  public notifyOrderUpdated(orderData: {
    orderId: string;
    orderNumber: string;
    status: string;
    tableNumber?: string;
    waiterId?: string;
  }): void {
    if (this.socket) {
      this.socket.emit('order_updated', orderData);
    }
  }

  /**
   * Send order ready notification
   */
  public notifyOrderReady(orderData: {
    orderId: string;
    orderNumber: string;
    tableNumber: string;
    waiterId?: string;
  }): void {
    if (this.socket) {
      this.socket.emit('order_ready', orderData);
    }
  }

  /**
   * Send payment received notification
   */
  public notifyPaymentReceived(paymentData: {
    paymentId: string;
    orderId: string;
    orderNumber?: string;
    amount: number;
    currency: string;
    method: string;
    waiterId?: string;
  }): void {
    if (this.socket) {
      this.socket.emit('payment_received', paymentData);
    }
  }

  /**
   * Send table status change notification
   */
  public notifyTableStatusChanged(tableData: {
    tableId: string;
    tableNumber: string;
    previousStatus: string;
    newStatus: string;
    waiterId?: string;
  }): void {
    if (this.socket) {
      this.socket.emit('table_status_changed', tableData);
    }
  }

  /**
   * Send kitchen update notification
   */
  public notifyKitchenUpdate(updateData: {
    type: string;
    message: string;
    priority?: 'low' | 'normal' | 'high' | 'urgent';
    data?: any;
  }): void {
    if (this.socket) {
      this.socket.emit('kitchen_update', updateData);
    }
  }

  /**
   * Send staff alert notification
   */
  public notifyStaffAlert(alertData: {
    title: string;
    message: string;
    priority?: 'low' | 'normal' | 'high' | 'urgent';
    targetUserId?: string;
    data?: any;
  }): void {
    if (this.socket) {
      this.socket.emit('staff_alert', alertData);
    }
  }

  /**
   * Send custom notification
   */
  public sendNotification(notificationData: {
    type: NotificationType;
    title: string;
    message: string;
    priority?: 'low' | 'normal' | 'high' | 'urgent';
    targetRoom?: string;
    targetUserId?: string;
    data?: any;
  }): void {
    if (this.socket) {
      this.socket.emit('send_notification', notificationData);
    }
  }

  /**
   * Ping server to check connection
   */
  public ping(): void {
    if (this.socket) {
      this.socket.emit('ping', (response: string) => {
        console.log('Ping response:', response);
      });
    }
  }

  /**
   * Update callbacks
   */
  public updateCallbacks(callbacks: Partial<NotificationServiceCallbacks>): void {
    this.callbacks = { ...this.callbacks, ...callbacks };
  }

  /**
   * Setup WebSocket event listeners
   */
  private setupEventListeners(): void {
    if (!this.socket) return;

    // Connection events
    this.socket.on('connect', () => {
      console.log('Connected to notification service');
      this.isConnected = true;
      this.authenticate();
      this.callbacks.onConnect?.();
    });

    this.socket.on('disconnect', (reason) => {
      console.log('Disconnected from notification service:', reason);
      this.isConnected = false;
      this.callbacks.onDisconnect?.();
    });

    // Authentication events
    this.socket.on('auth_success', (data) => {
      console.log('Authentication successful:', data);
      this.callbacks.onAuthSuccess?.(data);
    });

    this.socket.on('auth_error', (error) => {
      console.error('Authentication failed:', error);
      this.callbacks.onAuthError?.(error.message);
    });

    // Order notifications
    this.socket.on('order_created', (data) => {
      console.log('Order created notification:', data);
      this.callbacks.onOrderCreated?.(data);
      this.callbacks.onNotification?.(data);
    });

    this.socket.on('order_updated', (data) => {
      console.log('Order updated notification:', data);
      this.callbacks.onOrderUpdated?.(data);
      this.callbacks.onNotification?.(data);
    });

    this.socket.on('order_ready', (data) => {
      console.log('Order ready notification:', data);
      this.callbacks.onOrderReady?.(data);
      this.callbacks.onNotification?.(data);
    });

    this.socket.on('order_completed', (data) => {
      console.log('Order completed notification:', data);
      this.callbacks.onOrderCompleted?.(data);
      this.callbacks.onNotification?.(data);
    });

    // Payment notifications
    this.socket.on('payment_received', (data) => {
      console.log('Payment received notification:', data);
      this.callbacks.onPaymentReceived?.(data);
      this.callbacks.onNotification?.(data);
    });

    // Table notifications
    this.socket.on('table_status_changed', (data) => {
      console.log('Table status changed notification:', data);
      this.callbacks.onTableStatusChanged?.(data);
      this.callbacks.onNotification?.(data);
    });

    // Kitchen notifications
    this.socket.on('kitchen_update', (data) => {
      console.log('Kitchen update notification:', data);
      this.callbacks.onKitchenUpdate?.(data);
      this.callbacks.onNotification?.(data);
    });

    // Staff notifications
    this.socket.on('staff_alert', (data) => {
      console.log('Staff alert notification:', data);
      this.callbacks.onStaffAlert?.(data);
      this.callbacks.onNotification?.(data);
    });

    // System notifications
    this.socket.on('system_alert', (data) => {
      console.log('System alert notification:', data);
      this.callbacks.onSystemAlert?.(data);
      this.callbacks.onNotification?.(data);
    });

    // Error handling
    this.socket.on('error', (error) => {
      console.error('WebSocket error:', error);
      this.callbacks.onError?.(error.message);
    });

    this.socket.on('connect_error', (error) => {
      console.error('Connection error:', error);
      this.callbacks.onError?.(error.message);
    });

    // Ping/pong for connection health
    this.socket.on('pong', (data) => {
      console.log('Pong received:', data);
    });
  }

  /**
   * Get connection stats
   */
  public getConnectionInfo(): {
    isConnected: boolean;
    socketId?: string;
    transport?: string;
    userInfo?: typeof this.userInfo;
  } {
    return {
      isConnected: this.isConnected,
      socketId: this.socket?.id,
      transport: this.socket?.io.engine.transport.name,
      userInfo: this.userInfo,
    };
  }

  /**
   * Enable debug logging
   */
  public enableDebug(): void {
    if (this.socket) {
      this.socket.onAny((event, ...args) => {
        console.log(`WebSocket Event [${event}]:`, args);
      });
    }
  }

  /**
   * Disable debug logging
   */
  public disableDebug(): void {
    if (this.socket) {
      this.socket.offAny();
    }
  }
}

// Export singleton instance
export const notificationService = FrontendNotificationService.getInstance();